import type {
  CampaignConflictItem,
  ConflictingCampaign,
  IConflictResolution,
  ResolveConflictResult,
} from "@/@types/campaign-conflict";

/**
 * Tipos, constantes e helpers compartilhados para o fluxo de conflito de
 * produtos entre campanhas (detecção + resolução in-place no modal de criação).
 *
 * Centraliza o que antes estava duplicado nos forms de criação (tipo
 * `CampaignConflictItem`, `mergeConflicts`) e no modal.
 */
import { CAMPAIGN_TYPES } from "@/utils/campaign-types";

export type {
  CampaignConflictItem,
  ConflictingCampaign,
  ConflictResolutionFailReason,
  ResolveConflictResult,
} from "@/@types/campaign-conflict";

/**
 * Tipos de campanha dos quais é possível remover um SKU individual.
 * PRO013 (departamento/grupo) e PRO012 (legado) não permitem.
 */
export const REMOVABLE_CONFLICT_TYPES: string[] = [
  CAMPAIGN_TYPES.PRO014,
  CAMPAIGN_TYPES.PRO017,
];

/** Chave estável de uma campanha em conflito (inclui lista pra PRO017). */
export const conflictKey = (campaign: {
  type: string;
  code: number;
  storeCode: number;
  listNumber?: number;
}): string =>
  `${campaign.type}-${campaign.code}-${campaign.storeCode}-${campaign.listNumber ?? ""}`;

/** true quando ao menos uma campanha em conflito do item é removível. */
export const hasRemovableConflict = (conflict: CampaignConflictItem): boolean =>
  conflict.conflictingCampaigns.some((c) => REMOVABLE_CONFLICT_TYPES.includes(c.type));

/** true quando o item conflita também com campanhas não removíveis (PRO013). */
export const hasNonRemovableConflict = (conflict: CampaignConflictItem): boolean =>
  conflict.conflictingCampaigns.some((c) => !REMOVABLE_CONFLICT_TYPES.includes(c.type));

/**
 * Mescla conflitos do mesmo item vindos de chunks/fontes distintas e deduplica
 * as campanhas por `conflictKey`.
 */
export const mergeConflicts = (
  items: CampaignConflictItem[],
): CampaignConflictItem[] => {
  const map = new Map<string, CampaignConflictItem>();
  for (const conflict of items) {
    const existing = map.get(conflict.itemCode);
    if (!existing) {
      map.set(conflict.itemCode, {
        ...conflict,
        conflictingCampaigns: [...conflict.conflictingCampaigns],
      });
    } else {
      existing.conflictingCampaigns.push(...conflict.conflictingCampaigns);
    }
  }
  return Array.from(map.values()).map((conflict) => ({
    ...conflict,
    conflictingCampaigns: Array.from(
      new Map(
        conflict.conflictingCampaigns.map((campaign) => [conflictKey(campaign), campaign]),
      ).values(),
    ),
  }));
};

/**
 * Remove de um item da lista de conflitos as campanhas que já foram removidas
 * com sucesso, mantendo as que falharam/foram puladas (resolução parcial).
 */
export const dropResolvedCampaigns = (
  conflicts: CampaignConflictItem[],
  itemCode: string,
  removed: ResolveConflictResult["removed"],
): CampaignConflictItem[] => {
  const removedKeys = new Set(removed.map(conflictKey));
  return conflicts.map((conflict) =>
    conflict.itemCode !== itemCode
      ? conflict
      : {
          ...conflict,
          conflictingCampaigns: conflict.conflictingCampaigns.filter(
            (campaign) => !removedKeys.has(conflictKey(campaign)),
          ),
        },
  );
};

/* -------------------------------------------------------------------------- */
/* Staging — remoções marcadas no front e enviadas junto do create            */
/* -------------------------------------------------------------------------- */

/**
 * Remoções estagiadas: por item, as campanhas anteriores (removíveis) que serão
 * desativadas SOMENTE quando a campanha for criada (payload `conflictResolutions`).
 */
export type PendingRemoval = CampaignConflictItem;

/** Só as campanhas removíveis (PRO014/PRO017) de um conflito. */
export const removableCampaignsOf = (
  conflict: CampaignConflictItem,
): ConflictingCampaign[] =>
  conflict.conflictingCampaigns.filter((c) => REMOVABLE_CONFLICT_TYPES.includes(c.type));

/** Acumula um staging por item, deduplicando as campanhas por `conflictKey`. */
export const mergePendingRemoval = (
  pending: PendingRemoval[],
  staged: PendingRemoval,
): PendingRemoval[] => {
  const existing = pending.find((p) => p.itemCode === staged.itemCode);
  if (!existing) return [...pending, staged];
  const keys = new Set(existing.conflictingCampaigns.map(conflictKey));
  const merged = [
    ...existing.conflictingCampaigns,
    ...staged.conflictingCampaigns.filter((c) => !keys.has(conflictKey(c))),
  ];
  return pending.map((p) =>
    p.itemCode === staged.itemCode ? { ...p, conflictingCampaigns: merged } : p,
  );
};

/**
 * Reconcilia uma lista de conflitos (ex.: vinda da pré-validação) com o que já
 * está estagiado: tira as campanhas marcadas e descarta itens sem conflito restante.
 */
export const dropStagedCampaigns = (
  conflicts: CampaignConflictItem[],
  pending: PendingRemoval[],
): CampaignConflictItem[] => {
  const stagedByItem = new Map(
    pending.map((p) => [p.itemCode, new Set(p.conflictingCampaigns.map(conflictKey))]),
  );
  return conflicts
    .map((conflict) => {
      const staged = stagedByItem.get(conflict.itemCode);
      if (!staged) return conflict;
      return {
        ...conflict,
        conflictingCampaigns: conflict.conflictingCampaigns.filter(
          (campaign) => !staged.has(conflictKey(campaign)),
        ),
      };
    })
    .filter((conflict) => conflict.conflictingCampaigns.length > 0);
};

/**
 * Monta o payload `conflictResolutions` do create, considerando apenas itens que
 * ainda fazem parte da campanha (descartar um item estagiado o exclui do envio).
 */
export const buildConflictResolutions = (
  pending: PendingRemoval[],
  itemCodes: Set<string>,
): IConflictResolution[] =>
  pending
    .filter((p) => itemCodes.has(p.itemCode))
    .map((p) => ({
      itemCode: p.itemCode,
      conflictingCampaigns: p.conflictingCampaigns.map((c) => ({
        code: c.code,
        type: c.type,
        storeCode: c.storeCode,
        listNumber: c.listNumber,
      })),
    }));

/**
 * Descrição amigável para uma resolução parcial (nem tudo removido).
 * Prioriza o caso de mínimo de 2 itens; depois PRO013; senão genérico.
 */
export const describePartialResolution = (result: ResolveConflictResult): string => {
  const minTwo = result.failed.find((failure) => failure.reason === "MIN_TWO_ITEMS");
  if (minTwo) {
    return `A campanha #${minTwo.code} ficaria com menos de 2 produtos ativos. Edite-a manualmente.`;
  }
  if (result.skipped.length > 0) {
    return "Campanhas por departamento/grupo (PRO013) precisam de ajuste manual ou descarte o item.";
  }
  return "Algumas campanhas não puderam ser ajustadas. Tente novamente ou descarte o item.";
};

export const hasGradeConflict = (conflicts: CampaignConflictItem[]): boolean =>
  conflicts.some(conflict =>
    conflict.conflictingCampaigns.some(campaign => (campaign.grades?.length ?? 0) > 0),
  );
